import {
    type FC,
    type ReactElement,
    useCallback,
    useRef,
    useMemo,
    type MutableRefObject,
    useState,
} from 'react';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';
import classnames from 'classnames';

import {
    BuyerProductImageWithPrice,
    BuyerProductTile,
    TILE_SIZES,
    type ImageBackgroundColor,
    type TileSize,
    SRC_SET_SIZES_TILE,
} from 'dibs-buyer-product-tile/exports/BuyerProductTile.ts';
import { BuyerProductTileShimmer } from 'dibs-buyer-product-tile/exports/BuyerProductTileShimmer.ts';
import ItemHeartWrapper from 'dibs-portfolio-heart/exports/ItemHeartWrapper.js';
import { useFavoritesItem } from 'dibs-portfolio-folder/exports/useFavoritesItem.tsx';
import { type FavoritesItemPropsType } from 'dibs-portfolio-folder/exports/FavoritesItemProvider.tsx';
import { getBuyerId } from 'dibs-cookie-jar';
import ItemFolderWrapper from 'dibs-portfolio-folder/exports/ItemFolderWrapper.jsx';
import {
    trackingConstants,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
    trackEcommerce,
    trackEvent,
    getUserEventItem,
    trackUserEventItemAddToFavorites,
} from 'dibs-tracking';
import { useFolderTracking } from './useFolderTracking.ts';
import { useClientState } from 'dibs-react-hooks/src/useClientState.ts';

import { type CarouselItemTile_viewer$key } from './__generated__/CarouselItemTile_viewer.graphql.ts';
import { type CarouselItemTile_user$key } from './__generated__/CarouselItemTile_user.graphql.ts';
import { type CarouselItemTile_item$key } from './__generated__/CarouselItemTile_item.graphql.ts';

import dibsCss from 'dibs-css';

const { EVENT_SELECT_ITEM, EVENT_VIEW_ITEM_LIST, EVENT_WISHLIST } = eventNameConstants;
const { INTERACTION_TYPE_ITEM_FAVORITING } = interactionTypeConstants;
const {
    STEP_INTERACTION_ITEM_ADDED,
    STEP_INTERACTION_ITEM_REMOVED,
    STEP_INTERACTION_UNAUTH_SAVE_CLICK,
} = stepInteractionConstants;
const { ECOM_PRODUCT_CLICK, ECOM_PRODUCT_IMPRESSION } = trackingConstants;

const viewerFragment = graphql`
    fragment CarouselItemTile_viewer on Viewer
    @argumentDefinitions(
        # if favoritesComponentOverride is provided don't fetch favorites
        fetchFavorites: { type: "Boolean", defaultValue: false }
        userId: { type: "String", defaultValue: "" }
    ) {
        ...BuyerProductTile_viewer
        ...ItemHeartWrapper_viewer
            @arguments(userIds: [$userId], loadPortfolioData: $fetchFavorites)
    }
`;

const userFragment = graphql`
    fragment CarouselItemTile_user on User {
        serviceId
        ...BuyerProductTile_user @arguments(showSeller: false)
        ...useFavoritesItem_user
    }
`;

const itemFragment = graphql`
    fragment CarouselItemTile_item on Item
    @argumentDefinitions(
        # if favoritesComponentOverride is provided don't fetch favorites
        fetchFavorites: { type: "Boolean", defaultValue: false }
        userId: { type: "String", defaultValue: "" }
        isTrade: { type: "Boolean", defaultValue: false }
        priceBookName: { type: "String" }
    ) {
        serviceId
        firstPhotoWebPath(size: thumb)
        title
        ecommerceTrackingParams
        browseUrl
        categoryCode

        # This needs to match the fragment in ItemHeartWrapper so that when ItemHeartWrapper is refetched, this is also updated
        heartPortfolioItems: portfolioItems(userId: $userId, portfolioType: HEART)
            @include(if: $fetchFavorites) {
            portfolioId
        }
        ...ItemHeartWrapper_item @arguments(userId: $userId, loadPortfolioData: $fetchFavorites)
        ...ItemFolderWrapper_item @arguments(userId: $userId, loadPortfolioData: $fetchFavorites)
        ...BuyerProductTile_item
            @arguments(isTrade: $isTrade, priceBookName: $priceBookName, showSeller: false)
        ...BuyerProductImageWithPrice_item
            @arguments(isTrade: $isTrade, priceBookName: $priceBookName)
        ...useFavoritesItem_item
    }
`;

const VERTICAL_OFFSET_IMG = 500;

export const CarouselItemTile: FC<{
    viewer: CarouselItemTile_viewer$key;
    user?: CarouselItemTile_user$key | null;
    item?: CarouselItemTile_item$key | null;
    currency: string;
    authModalLoader: $TSFixMe;
    imageBackgroundColor: ImageBackgroundColor;
    index: number;
    isMobile?: boolean;
    lazyLoadStartIndex: number;
    tileSize?: TileSize;
    showQuickView?: boolean;
    trackingList?: string;
    showFolder?: boolean;
    favoritesComponentOverride?: (props: FavoritesItemPropsType) => ReactElement | null;
    trackedImpressionsRef: MutableRefObject<Set<string>>;
    isSponsored?: boolean;
    onClick?: (itemId: string | null | undefined) => void;
    onContentVisible?: (itemId: string | null | undefined) => void;
    dataTn?: string;
    tileClassName?: string;
    imageWithPrice?: boolean;
}> = ({
    viewer: viewerRef,
    user: userRef,
    item: itemRef,
    currency,
    authModalLoader,
    imageBackgroundColor,
    index,
    isMobile,
    lazyLoadStartIndex,
    tileSize = TILE_SIZES.small,
    showQuickView = false,
    trackingList,
    showFolder = false,
    favoritesComponentOverride,
    trackedImpressionsRef,
    isSponsored,
    onClick,
    onContentVisible,
    dataTn,
    tileClassName,
    imageWithPrice = false,
}) => {
    const environment = useRelayEnvironment();
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const item = useFragment(itemFragment, itemRef);

    const containerRef = useRef(null);

    const isClient = useClientState();
    const {
        serviceId: itemId,
        browseUrl,
        categoryCode,
        ecommerceTrackingParams,
        title,
    } = item || {};

    const theme = isMobile ? 'circle-frosted-small' : 'circle-frosted-medium';

    const userEventItem = useMemo(
        () =>
            getUserEventItem({
                id: itemId,
                amount: ecommerceTrackingParams?.price,
                convertedAmounts: ecommerceTrackingParams?.convertedAmounts,
            }),
        [ecommerceTrackingParams, itemId]
    );

    const trackUserAddedItemToFavorites = useCallback(() => {
        if (userEventItem) {
            trackUserEventItemAddToFavorites(environment, [userEventItem]);
        }
    }, [environment, userEventItem]);

    const [isLoginFlowInProgress, setIsLoginFlowInProgress] = useState(false);
    const heartAuthModalShow = useCallback(() => {
        trackEvent({
            eventName: EVENT_WISHLIST,
            interaction_type: INTERACTION_TYPE_ITEM_FAVORITING,
            step_interaction_name: STEP_INTERACTION_UNAUTH_SAVE_CLICK,
            trigger: trackingList,
            isInteractiveEvent: true,
            ...(itemId && { additional: { itemId } }),
        });
        authModalLoader
            ?.show({
                flow: authModalLoader?.constants?.SAVE_ITEM_FLOW,
            })
            .then(() => {
                // This is a hack to handle favoriting after login. This helps to retain login flow state
                // if heart component is unmounted and mounted again. Remounting can happen if root query refetches all page data after login.
                setIsLoginFlowInProgress(true);
            });
    }, [authModalLoader, itemId, trackingList]);

    const folderModalShow = useCallback(
        () =>
            new Promise(async (resolve, reject) => {
                const onSuccess = (): Promise<void> =>
                    import(
                        /* webpackChunkName: "AddToFolderModalWrapper" */
                        'dibs-portfolio-folder/src/components/AddToFolderModalWrapper/index.js'
                    )
                        .then(exported => exported.default)
                        .then(resolve);

                if (!getBuyerId(document.cookie)) {
                    const { userId } = await authModalLoader?.show({
                        flow: authModalLoader?.constants?.SAVE_ITEM_TO_FOLDER_FLOW,
                        shouldPauseAuthFlow: (state: { step: number }) => state.step === 0,
                    });
                    if (!userId) {
                        reject();
                    } else {
                        onSuccess();
                    }
                } else {
                    onSuccess();
                }
            }),
        [authModalLoader]
    );

    const folderAddItemSuccess = useCallback(() => {
        if (authModalLoader.isPaused()) {
            authModalLoader.resume();
        }
        trackUserAddedItemToFavorites();
    }, [authModalLoader, trackUserAddedItemToFavorites]);

    const onItemClick = useCallback(() => {
        if (!trackingList) {
            return;
        }

        trackEcommerce({
            type: ECOM_PRODUCT_CLICK,
            eventName: EVENT_SELECT_ITEM,
            trigger: trackingList,
            actionField: { list: trackingList, itemId },
            products: [
                {
                    position: index + 1,
                    categoryCode,
                    category: browseUrl,
                    dimension141: isSponsored,
                    ...ecommerceTrackingParams,
                    name: title,
                },
            ],
        });
        onClick?.(itemId);
    }, [
        browseUrl,
        categoryCode,
        ecommerceTrackingParams,
        index,
        itemId,
        title,
        onClick,
        trackingList,
        isSponsored,
    ]);

    const onVisibilityChange = useCallback(() => {
        if (!trackingList || !itemId || trackedImpressionsRef.current.has(itemId)) {
            return;
        }

        trackedImpressionsRef.current.add(itemId);
        trackEcommerce({
            type: ECOM_PRODUCT_IMPRESSION,
            eventName: EVENT_VIEW_ITEM_LIST,
            products: [
                {
                    position: index + 1,
                    list: trackingList,
                    categoryCode: categoryCode,
                    category: browseUrl,
                    dimension141: isSponsored,
                    ...ecommerceTrackingParams,
                    name: title,
                },
            ],
            trigger: trackingList,
        });
        onContentVisible?.(itemId);
    }, [
        browseUrl,
        categoryCode,
        ecommerceTrackingParams,
        index,
        title,
        trackingList,
        itemId,
        onContentVisible,
        trackedImpressionsRef,
        isSponsored,
    ]);

    const handleFavorited = useCallback(
        ({ type }: { type: 'favorited' | 'unFavorited' }): (() => void) => {
            return (): void => {
                if (type === 'favorited') {
                    trackUserAddedItemToFavorites();
                }
                if (!trackingList) {
                    return;
                }

                const step_interaction_name =
                    type === 'favorited'
                        ? STEP_INTERACTION_ITEM_ADDED
                        : STEP_INTERACTION_ITEM_REMOVED;

                trackEvent(
                    {
                        action: step_interaction_name,
                        label: trackingList,
                        category: INTERACTION_TYPE_ITEM_FAVORITING,
                        isInteractiveEvent: true,
                        additional: { itemId },
                        eventName: EVENT_WISHLIST,
                        interaction_type: INTERACTION_TYPE_ITEM_FAVORITING,
                        step_interaction_name,
                        trigger: trackingList,
                    },
                    null
                );
            };
        },
        [trackingList, itemId, trackUserAddedItemToFavorites]
    );

    const folderTracking = useFolderTracking(trackingList);

    const {
        isFolderVisible,
        onFavorited,
        onUnFavorited,
        onFolderActionStart,
        onFolderActionEnd,
        onMouseEnter,
        onMouseLeave,
        showFolderPlusIcon,
    } = useFavoritesItem({
        item,
        user,
        applyMouseEvents: true,
        tileElementRef: containerRef,
    });

    if (!item) {
        return <BuyerProductTileShimmer size={tileSize} imageOnly={imageWithPrice} />;
    }

    const favoritesComponent = favoritesComponentOverride?.({
        isFolderVisible,
        showFolderPlusIcon,
        onFavorited,
        onUnFavorited,
        onFolderActionStart,
        onFolderActionEnd,
    }) ?? (
        <div className={classnames(dibsCss.absolute, dibsCss.top0, dibsCss.right0, dibsCss.zBump)}>
            <div className={classnames(dibsCss.relative, dibsCss.ptXxsmall, dibsCss.prXxsmall)}>
                <ItemHeartWrapper
                    theme={theme}
                    itemId={itemId}
                    userId={user?.serviceId}
                    viewer={viewer}
                    item={item}
                    authModalLoader={authModalLoader}
                    authModalShow={heartAuthModalShow}
                    onFavorited={handleFavorited({ type: 'favorited' })}
                    onUnFavorited={handleFavorited({ type: 'unFavorited' })}
                    isLoginFlowInProgress={isLoginFlowInProgress}
                />
            </div>
            {showFolder && isFolderVisible && (
                <div className={classnames(dibsCss.relative, dibsCss.ptXxsmall, dibsCss.prXxsmall)}>
                    <ItemFolderWrapper
                        theme={theme}
                        singlePortfolioItem
                        showPlusIcon
                        selectedItemImages={[item.firstPhotoWebPath]}
                        selectedItemTitles={[item.title]}
                        item={item}
                        userId={user?.serviceId}
                        userIds={[user?.serviceId]}
                        getModalComponent={folderModalShow}
                        onAddItemSuccess={folderAddItemSuccess}
                        authModalTrackingLabel={trackingList}
                        trackingHooks={folderTracking}
                    />
                </div>
            )}
        </div>
    );

    return (
        <div
            ref={containerRef}
            className={classnames(dibsCss.relative, dibsCss.wFull, tileClassName)}
            data-tn={dataTn ? `${dataTn}-product-tile` : undefined}
        >
            {imageWithPrice ? (
                <BuyerProductImageWithPrice
                    item={item}
                    currency={currency}
                    imageBackgroundColor={imageBackgroundColor}
                    imageLoadVerticalOffset={VERTICAL_OFFSET_IMG}
                    index={index}
                    itemsPerRow={lazyLoadStartIndex}
                    onClick={onItemClick}
                    onContentLoaded={onVisibilityChange}
                    size={tileSize}
                    srcSet={{ sizes: SRC_SET_SIZES_TILE }}
                    favoritesComponent={favoritesComponent}
                />
            ) : (
                <BuyerProductTile
                    viewer={viewer}
                    itemSearch={null}
                    item={item}
                    user={user}
                    afterDisplay={isClient}
                    currency={currency}
                    imageBackgroundColor={imageBackgroundColor}
                    imageLoadVerticalOffset={VERTICAL_OFFSET_IMG}
                    index={index}
                    isSponsored={isSponsored}
                    itemsPerRow={lazyLoadStartIndex}
                    onClick={onItemClick}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onContentVisible={onVisibilityChange}
                    size={tileSize}
                    showMskuSwatch={false}
                    showMeasurements={false}
                    showPrice={isClient}
                    hasQuickView={showQuickView}
                    showQuickViewButton={showQuickView}
                    showShippingCost={isClient}
                    srcSet={{ sizes: SRC_SET_SIZES_TILE }}
                    favoritesComponent={favoritesComponent}
                    tileElementRef={containerRef}
                />
            )}
        </div>
    );
};
